import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { isProcessing } from "./campaignModel";

export const useCampaigns = (userId) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${userId}`);
      setCampaigns(res.data.campaigns || []);
    } catch (e) {
      console.error("Error loading campaigns:", e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns/templates/${userId}`);
      setTemplates(res.data.templates || []);
    } catch (e) {
      console.error("Error loading templates:", e);
    }
  }, [userId]);

  useEffect(() => { fetchCampaigns(); fetchTemplates(); }, [fetchCampaigns, fetchTemplates]);
  useEffect(() => stopPolling, [stopPolling]);

  const fetchDetail = useCallback(async (campaignId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/detail/${campaignId}`);
      setCampaignDetail(res.data);
      if (isProcessing(res.data.campaign?.status) && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          try {
            const r = await axios.get(`${API}/campaigns/detail/${campaignId}`);
            setCampaignDetail(r.data);
            if (!isProcessing(r.data.campaign?.status)) {
              stopPolling();
              fetchCampaigns();
            }
          } catch (e) { console.warn("Campaign poll failed, will retry:", e?.message || e); }
        }, 3000);
      }
    } catch (e) {
      toast.error("Failed to load campaign details");
    } finally {
      setDetailLoading(false);
    }
  }, [fetchCampaigns, stopPolling]);

  const openCampaign = useCallback((campaignId) => {
    setSelectedCampaign(campaignId);
    fetchDetail(campaignId);
  }, [fetchDetail]);

  const closeCampaign = useCallback(() => {
    stopPolling();
    setSelectedCampaign(null);
    setCampaignDetail(null);
  }, [stopPolling]);

  const afterCreate = (res) => {
    fetchCampaigns();
    openCampaign(res.data.campaign.campaignId);
    return true;
  };

  const createFromCSV = async (formData) => {
    try {
      const res = await axios.post(`${API}/campaigns/create`, formData);
      toast.success(`Campaign created — ${res.data.prospectsImported} prospects imported`);
      return afterCreate(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      return false;
    }
  };

  const createFromLeads = async (payload) => {
    try {
      const res = await axios.post(`${API}/campaigns/create-from-leads`, { userId, ...payload });
      toast.success(`Campaign created — ${res.data.prospectsImported} prospects`);
      return afterCreate(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Creation failed");
      return false;
    }
  };

  const saveTemplate = async (name) => {
    try {
      await axios.post(`${API}/campaigns/templates`, { userId, name, campaignId: selectedCampaign });
      toast.success("Cadence saved as a template");
      fetchTemplates();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
      return false;
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/campaigns/templates/${templateId}`);
      fetchTemplates();
      toast.info("Template removed");
    } catch (e) {
      toast.error("Failed to remove template");
    }
  };

  const approveEmail = async (item, edits = {}) => {
    try {
      await axios.post(`${API}/campaigns/approve-email`, {
        campaignId: selectedCampaign,
        prospectId: item.prospectId,
        stepNum: item.stepNum,
        editedSubject: edits.editedSubject ?? null,
        editedBody: edits.editedBody ?? null,
      });
      toast.success(`Email sent to ${item.contactEmail}`);
      fetchDetail(selectedCampaign);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
      return false;
    }
  };

  const skipProspect = async (prospectId) => {
    try {
      await axios.post(`${API}/campaigns/skip-prospect/${prospectId}`);
      toast.info("Prospect skipped");
      fetchDetail(selectedCampaign);
    } catch (e) {
      toast.error("Failed to skip");
    }
  };

  const deleteCampaign = async () => {
    try {
      await axios.delete(`${API}/campaigns/${selectedCampaign}`);
      toast.success("Campaign deleted");
      closeCampaign();
      fetchCampaigns();
    } catch (e) {
      toast.error("Failed to delete campaign");
    }
  };

  return {
    campaigns, loading, templates, selectedCampaign, campaignDetail, detailLoading,
    fetchTemplates, openCampaign, closeCampaign,
    createFromCSV, createFromLeads, saveTemplate, deleteTemplate,
    approveEmail, skipProspect, deleteCampaign,
  };
};
